import { useState, useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

interface SubtleHintProps {
  hints?: string[];
  delay?: number;
  className?: string;
}

const SubtleHint = ({ hints, delay = 6000, className = '' }: SubtleHintProps) => {
  const [visible, setVisible] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const { t } = useLanguage();

  // Default contextual hints with translations
  const defaultHints = [
    t('hint_click_enter') || 'Click anywhere to enter',
    t('hint_take_time') || 'Take your time',
    t('hint_each_room') || 'Each room holds a question',
    t('hint_no_rush') || 'There is no rush here',
  ];

  const allHints = hints && hints.length > 0 ? hints : defaultHints;

  useEffect(() => {
    // Check if user has interacted before
    const interacted = localStorage.getItem('has_interacted');
    if (interacted) {
      setHasInteracted(true);
      return;
    }

    // Show hint after delay (user idle)
    const showTimer = setTimeout(() => setVisible(true), delay);

    // Cycle through hints with fade
    const cycleTimer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentHint(prev => (prev + 1) % allHints.length);
        setVisible(true);
      }, 600);
    }, 8000);

    // Hide on interaction
    const handleInteraction = () => {
      setVisible(false);
      setHasInteracted(true);
      localStorage.setItem('has_interacted', 'true');
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('keydown', handleInteraction);
    window.addEventListener('scroll', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    return () => {
      clearTimeout(showTimer);
      clearInterval(cycleTimer);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [delay, allHints.length]);

  if (hasInteracted || !visible) return null;

  return (
    <p 
      className={`text-xs text-muted-foreground/40 transition-all duration-700 
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}
        ${className}`}
      role="status"
      aria-live="polite"
    >
      {allHints[currentHint]}
    </p>
  );
};

export default SubtleHint;
